import React, { useMemo, useState } from 'react'
import { formatPixeFull } from './utils'
import { FaCopy, FaArrowUp, FaArrowDown } from 'react-icons/fa'

type Props = {
  fiatEstimate?: string | number
  pixeBalance?: string | number
  change24hPct?: string | number
  ss58?: string
  onReceive?: () => void
  onCopy?: () => void
  onSend?: () => void
}

function formatCurrency(n?: string | number) {
  const v = typeof n === 'string' ? Number(n) : n
  if (!Number.isFinite(v as number)) return String(n ?? '0')
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v as number)
}

function shorten(addr?: string) {
  if (!addr) return ''
  // ASCII only to avoid mojibake in some builds/fonts.
  return addr.length > 20 ? `${addr.slice(0, 12)}...${addr.slice(-6)}` : addr
}

export default function SummaryCard({ fiatEstimate, pixeBalance, change24hPct, ss58, onReceive, onCopy, onSend }: Props) {
  const [copied, setCopied] = useState(false)
  const changeMeta = useMemo(() => {
    const n = typeof change24hPct === 'string' ? Number(change24hPct) : change24hPct
    if (!Number.isFinite(n as number)) return { text: '--', up: false, neutral: true }
    const up = (n as number) >= 0
    const text = `${up ? '+' : ''}${(n as number).toFixed(2)}%`
    return { text, up, neutral: (n as number) === 0 }
  }, [change24hPct])

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-white/70 text-sm">Total balance</div>
          <div className="text-3xl font-extrabold text-white">{formatCurrency(fiatEstimate)}</div>
          <div className="text-xs text-white/65 mt-1">PIXE {formatPixeFull(pixeBalance, 2)}</div>
        </div>
        <div>
          <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold uppercase ${changeMeta.neutral ? 'bg-white/20 text-white' : changeMeta.up ? 'bg-emerald-300 text-neutral-950' : 'bg-rose-300 text-neutral-950'}`}>{changeMeta.text}</span>
        </div>
      </div>

      <div className="mt-4 flex items-center flex-wrap gap-2 text-sm">
        {/* Copy address chip (mobile-style) */}
        <button
          onClick={async () => {
            try { await onCopy?.(); setCopied(true); setTimeout(()=> setCopied(false), 1200) } catch { /* noop */ }
          }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-white/90 hover:bg-white/15"
        >
          <FaCopy size={14} className="text-white/70" />
          <span className="font-medium">{copied ? 'Copied!' : shorten(ss58)}</span>
        </button>

        {/* Send / Receive (mobile-style neutral with colored arrows) */}
        {onSend && (
          <button
            onClick={onSend}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/10 text-white hover:bg-white/15 font-semibold"
          >
            <FaArrowUp size={18} className="text-rose-400" />
            Send
          </button>
        )}
        <button
          onClick={onReceive}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/10 text-white hover:bg-white/15 font-semibold"
        >
          <FaArrowDown size={18} className="text-emerald-400" />
          Receive
        </button>
      </div>
    </div>
  )
}

